import Dexie from 'dexie'

/**
 * Device cache for the passphrase-derived AES-KW key. The key is
 * non-extractable and stored in IndexedDB so the user only re-enters the
 * passphrase on a new device or after storage is cleared.
 *
 * A separate database from the legacy `minimind_key_store` keeps this cache
 * independent of the seed-derived device key.
 */

const DB_NAME = 'minimind_passphrase_key_store'
const STORE_NAME = 'passphrase_keys'
const CACHE_KEY = 'passphrase'

let db: Dexie | null = null

function getDb(): Dexie {
  if (!db) {
    db = new Dexie(DB_NAME)
    db.version(1).stores({ [STORE_NAME]: 'key' })
  }
  return db
}

/**
 * Caches the non-extractable passphrase key on the device.
 */
export async function cachePassphraseKey(key: CryptoKey): Promise<void> {
  await getDb().table(STORE_NAME).put({ key: CACHE_KEY, cryptoKey: key })
}

/**
 * Loads the cached passphrase key, or null when there is none.
 */
export async function loadPassphraseKey(): Promise<CryptoKey | null> {
  const row = await getDb().table(STORE_NAME).get(CACHE_KEY)
  return row?.cryptoKey ?? null
}

/**
 * Removes the cached passphrase key (sign-out or storage reset).
 */
export async function clearPassphraseKey(): Promise<void> {
  await getDb().table(STORE_NAME).delete(CACHE_KEY)
}
